import { COPILOT_AGENT_SETTINGS_KEY } from './copilot.constants';
import type {
  AdvisorPersonaId,
  CopilotAgentSettings,
  TempoPersonalityId
} from './copilot.types';

export const DEFAULT_COPILOT_AGENT_SETTINGS: CopilotAgentSettings = {
  advisorPersona: 'maya',
  tempoPersonality: 'manager_coach',
  decisionEngineEnabled: true,
  agentLongNarrativeEnabled: true,
};

const PERSONALITY_IDS: ReadonlySet<string> = new Set([
  'manager_coach',
  'executive_brief',
  'analyst_detailed',
]);

const ADVISOR_IDS: ReadonlySet<string> = new Set([
  'kevin',
  'maya',
  'alex',
  'sam',
]);

function isTempoPersonalityId(value: unknown): value is TempoPersonalityId {
  return typeof value === 'string' && PERSONALITY_IDS.has(value);
}

function isAdvisorPersonaId(value: unknown): value is AdvisorPersonaId {
  return typeof value === 'string' && ADVISOR_IDS.has(value);
}

/** Map advisor → legacy tempoPersonality bridge for the gateway. */
export function tempoPersonalityForAdvisor(
  advisor: AdvisorPersonaId,
): TempoPersonalityId {
  switch (advisor) {
    case 'alex':
      return 'executive_brief';
    case 'sam':
      return 'analyst_detailed';
    case 'kevin':
    case 'maya':
    default:
      return 'manager_coach';
  }
}

/** Infer advisor from a legacy tempoPersonality value. */
export function advisorFromTempoPersonality(
  personality: TempoPersonalityId,
): AdvisorPersonaId {
  if (personality === 'executive_brief') return 'alex';
  if (personality === 'analyst_detailed') return 'sam';
  return 'maya';
}

/** Load persisted Tempo agent preferences (advisor + intelligence toggles). */
export function loadCopilotAgentSettings(): CopilotAgentSettings {
  try {
    const raw = localStorage.getItem(COPILOT_AGENT_SETTINGS_KEY);
    if (!raw) {
      return { ...DEFAULT_COPILOT_AGENT_SETTINGS };
    }
    const parsed = JSON.parse(raw) as Partial<CopilotAgentSettings>;
    const advisorPersona = isAdvisorPersonaId(parsed.advisorPersona)
      ? parsed.advisorPersona
      : isTempoPersonalityId(parsed.tempoPersonality)
        ? advisorFromTempoPersonality(parsed.tempoPersonality)
        : DEFAULT_COPILOT_AGENT_SETTINGS.advisorPersona;
    return {
      advisorPersona,
      tempoPersonality: tempoPersonalityForAdvisor(advisorPersona),
      // Decision engine is always on — the UI toggle was removed; ignore any
      // previously persisted opt-out.
      decisionEngineEnabled: true,
      // Long answers always on — the UI toggle was removed; ignore any
      // previously persisted opt-out.
      agentLongNarrativeEnabled: true,
    };
  } catch {
    return { ...DEFAULT_COPILOT_AGENT_SETTINGS };
  }
}

export function saveCopilotAgentSettings(settings: CopilotAgentSettings): void {
  localStorage.setItem(COPILOT_AGENT_SETTINGS_KEY, JSON.stringify(settings));
}
